import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CATEGORIES } from "../../data/courses";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import styles from "./Navbar.module.css";
import CategoryIcon from "../../components/ui/CategoryIcon";

export default function Navbar() {
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [cartOpen, setCartOpen]       = useState(false);
  const [scrolled, setScrolled]       = useState(false);

  const { cart, removeFromCart, cartTotal } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef   = useRef(null);

  // Close everything on route change
  useEffect(() => {
    setMobileOpen(false);
    setCoursesOpen(false);
    setCartOpen(false);
  }, [location]);

  // Scroll shadow
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Single outside-click handler on the whole nav
  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setCoursesOpen(false);
        setCartOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav ref={navRef} className={[styles.nav, scrolled ? styles.navScrolled : ""].join(" ")}>
      <div className={styles.inner}>

        {/* Logo */}
        <Link to="/" className={styles.logo}>
          <span className={styles.logoMark}>
            <img
              src="/logo.svg"
              alt="Shortly Life"
              onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerText = "⚡"; }}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </span>
          <span className={styles.logoText}>Shortly Life</span>
        </Link>

        {/* Desktop Links */}
        <div className={styles.links}>
          <div className={styles.dropWrap}>
            <button
              className={[styles.navLink, coursesOpen ? styles.navLinkActive : ""].join(" ")}
              onClick={() => { setCoursesOpen(v => !v); setCartOpen(false); }}
            >
              Our Courses
              <span className={[styles.chevron, coursesOpen ? styles.chevronUp : ""].join(" ")}>▾</span>
            </button>

            {coursesOpen && (
              <div className={styles.dropdown}>
                <p className={styles.dropLabel}>Browse by category</p>
                <div className={styles.dropGrid}>
                  {CATEGORIES.map((cat) => (
                    <Link key={cat.id} to={`/category/${cat.id}`} className={styles.dropItem}>
                      <span className={styles.dropIcon}><CategoryIcon icon={cat.icon} size={24} /></span>
                      <span>
                        <strong>{cat.label}</strong>
                        <small>{cat.description}</small>
                      </span>
                    </Link>
                  ))}
                </div>
                <div className={styles.dropFooter}>
                  <Link to="/category/all" className={styles.dropAll}>View all courses →</Link>
                </div>
              </div>
            )}
          </div>

          <Link to="/about"   className={styles.navLink}>About Us</Link>
          <Link to="/contact" className={styles.navLink}>Contact</Link>

          {user ? (
            <>
              <Link to="/my-courses" className={styles.navLink} style={{ color: "var(--accent3)" }}>
                📚 My Courses
              </Link>
              <button
                onClick={handleLogout}
                className={styles.navLink}
                style={{ cursor: "pointer", color: "var(--red)", background: "none", border: "none" }}
              >
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className={styles.navLink}>Login</Link>
          )}
        </div>

        {/* Cart + Hamburger */}
        <div className={styles.actions}>
          <div className={styles.cartWrap}>
            <button className={styles.cartBtn} onClick={() => { setCartOpen(v => !v); setCoursesOpen(false); }}>
              🛒
              {cart.length > 0 && <span className={styles.cartBadge}>{cart.length}</span>}
            </button>

            {cartOpen && (
              <div className={styles.cartDrop}>
                <p className={styles.cartTitle}>Your Cart ({cart.length})</p>
                {cart.length === 0 ? (
                  <p className={styles.cartEmpty}>No courses added yet.</p>
                ) : (
                  <>
                    <div className={styles.cartList}>
                      {cart.map((c) => (
                        <div key={c.id} className={styles.cartItem}>
                          <div>
                            <p className={styles.cartItemTitle}>{c.title}</p>
                            <p className={styles.cartItemPrice}>₹{c.price}</p>
                          </div>
                          <button className={styles.cartRemove} onClick={() => removeFromCart(c.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                    <div className={styles.cartFooter}>
                      <div className={styles.cartTotal}>
                        <span>Total</span>
                        <strong>₹{cartTotal}</strong>
                      </div>
                      <button className={styles.checkoutBtn} onClick={() => { setCartOpen(false); navigate("/checkout"); }}>
                        Proceed to Checkout →
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <button className={styles.hamburger} onClick={() => setMobileOpen(v => !v)} aria-label="Toggle menu">
            <span className={[styles.bar, mobileOpen ? styles.barX1 : ""].join(" ")} />
            <span className={[styles.bar, mobileOpen ? styles.barHide : ""].join(" ")} />
            <span className={[styles.bar, mobileOpen ? styles.barX2 : ""].join(" ")} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className={styles.mobileMenu}>
          <p className={styles.mobileSection}>Categories</p>
          {CATEGORIES.map((cat) => (
            <Link key={cat.id} to={`/category/${cat.id}`} className={styles.mobileLink}>
              <CategoryIcon icon={cat.icon} size={18} /> {cat.label}
            </Link>
          ))}
          <div className={styles.mobileDivider} />
          <Link to="/about"   className={styles.mobileLink}>About Us</Link>
          <Link to="/contact" className={styles.mobileLink}>Contact</Link>

          {user ? (
            <>
              <Link to="/my-courses" className={styles.mobileLink} style={{ color: "var(--accent3)" }}>
                📚 My Courses
              </Link>
              <button
                onClick={handleLogout}
                className={styles.mobileLink}
                style={{
                  cursor: "pointer",
                  color: "var(--red)",
                  background: "none",
                  border: "none",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                🚪 Logout
              </button>
            </>
          ) : (
            <Link to="/login" className={styles.mobileLink}>🔐 Login</Link>
          )}

          {cart.length > 0 && (
            <button className={styles.mobileCheckout} onClick={() => navigate("/checkout")}>
              🛒 Checkout ({cart.length} items · ₹{cartTotal})
            </button>
          )}
        </div>
      )}
    </nav>
  );
}